import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ImageManager from '../../components/admin/ImageManager';
import AIAssist, { useAIStatus } from '../../components/admin/AIAssist';
import MockupModal from '../../components/admin/MockupModal';
import { OptionsEditor, PersonalizationEditor } from '../../components/admin/OptionsEditor';
import ProductPicker, { useProductTitles } from '../../components/admin/ProductPicker';
import {
  API, withCreds, inputCls, smallInputCls, selectCls, btnPrimary, btnSecondary,
  Field, Modal, PageHeader, Spinner, money, errMsg, downloadCsv,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, Search, Check, X, Package, Upload, Download, Copy, Table2, AlertTriangle, FileSpreadsheet, Wand2, Sparkles } from 'lucide-react';
import Select from '../../components/FlatSelect';

const EMPTY_PRODUCT = {
  title: '', description: '', price: 0, compare_price: null, cost: null, sku: '',
  images: [], tags: [], category: '', inventory: 0, track_inventory: true,
  weight_oz: null, length_in: null, width_in: null, height_in: null,
  published: false, featured: false, variants: [],
  fulfillment: 'in_house', supplier: '', supplier_sku: '',
  options: [], personalizations: [], seo_title: '', seo_description: '', bundle_items: [],
};

function BundleSection({ items, price, productId, onChange, defaultOpen }) {
  const titles = useProductTitles(items.map(i => i.product_id));
  const value = items.reduce((sum, i) => sum + (titles[i.product_id]?.price || 0) * i.quantity, 0);
  return (
    <details className="rounded-xl border border-ink/10 p-4" open={defaultOpen || items.length > 0}>
      <summary className="text-ink/75 text-sm font-bold cursor-pointer">Bundle contents <span className="text-ink/45 font-normal">(makes this product a bundle)</span></summary>
      <div className="mt-4 space-y-2">
        <p className="text-ink/45 text-xs">Add the items included in this bundle. Their stock is used instead of this product's own quantity, and items with options can't be included.</p>
        <ProductPicker value={items} withQuantity onChange={onChange}
          filter={p => p.id !== productId && !(p.bundle_items?.length) && !(p.options?.length)} placeholder="Search items to include…" />
        {items.length > 0 && (
          <p className="text-ink/60 text-sm">Items bought separately: <b className="text-ink">{money(value)}</b>
            {value > price && <> · Bundle saves shoppers <b className="text-green-600">{money(value - price)} ({Math.round((1 - price / value) * 100)}%)</b></>}
            {value > 0 && value <= price && <span className="text-amber-600"> · Bundle price isn't lower than buying separately</span>}
          </p>
        )}
      </div>
    </details>
  );
}

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

// ── Product editor ────────────────────────────────────────────────
function ProductModal({ product, categories, onClose, onSave, startAsBundle }) {
  const [form, setForm] = useState({ ...EMPTY_PRODUCT, ...(product || {}) });
  const [tagsText, setTagsText] = useState((product?.tags || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMockups, setShowMockups] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const applyAI = (patch) => {
    if (patch.tags) setTagsText(patch.tags.join(', '));
    setForm(f => ({ ...f, ...patch }));
  };

  const margin = form.cost != null && form.price > 0 ? Math.round(((form.price - form.cost) / form.price) * 100) : null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, tags: tagsText.split(',').map(t => t.trim()).filter(Boolean) });
    } catch (err) {
      setError(errMsg(err, 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={product ? 'Edit Product' : 'Add Product'} onClose={onClose} width="max-w-3xl">
      {showMockups && <MockupModal images={form.images} onClose={() => setShowMockups(false)} onAdd={urls => set('images', [...form.images, ...urls])} />}
      <form onSubmit={handleSave} className="space-y-6">
        <AIAssist form={{ ...form, tags: tagsText.split(',').map(t => t.trim()).filter(Boolean) }} productId={product?.id} onApply={applyAI} />
        <section className="grid grid-cols-2 gap-4">
          <Field label="Title *" className="col-span-2">
            <input value={form.title} onChange={e => set('title', e.target.value)} required data-testid="product-title-input" className={inputCls} />
          </Field>
          <Field label="Description" className="col-span-2">
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={4} className={`${inputCls} resize-y`} />
          </Field>
          <Field label="Category" hint="Pick one, or type a new name to create it">
            <input value={form.category} onChange={e => set('category', e.target.value)} list="product-categories" className={inputCls} />
            <datalist id="product-categories">{categories.map(c => <option key={c.id} value={c.name} />)}</datalist>
          </Field>
          <Field label="Tags" hint="Comma separated">
            <input value={tagsText} onChange={e => setTagsText(e.target.value)} className={inputCls} />
          </Field>
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-3">Pricing</h3>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Price *">
              <input type="number" step="0.01" min="0" value={form.price} onChange={e => set('price', parseFloat(e.target.value) || 0)} required data-testid="product-price-input" className={inputCls} />
            </Field>
            <Field label="Compare-at price" hint="Shows as a strikethrough">
              <input type="number" step="0.01" min="0" value={form.compare_price ?? ''} onChange={e => set('compare_price', numOrNull(e.target.value))} className={inputCls} />
            </Field>
            <Field label="Cost (internal)" hint={margin != null ? `Margin ${margin}%` : 'Never shown to customers'}>
              <input type="number" step="0.01" min="0" value={form.cost ?? ''} onChange={e => set('cost', numOrNull(e.target.value))} className={inputCls} />
            </Field>
          </div>
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-3">Inventory</h3>
          <div className="grid grid-cols-3 gap-4 items-end">
            <Field label="SKU" hint="Must be unique">
              <input value={form.sku} onChange={e => set('sku', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Quantity in stock" hint={form.bundle_items?.length ? 'Bundles use their items\' stock' : ''}>
              <input type="number" value={form.inventory} disabled={!form.track_inventory || form.bundle_items?.length > 0} onChange={e => set('inventory', parseInt(e.target.value) || 0)} className={`${inputCls} disabled:opacity-40`} />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer pb-3">
              <input type="checkbox" checked={form.track_inventory} onChange={e => set('track_inventory', e.target.checked)} className="w-4 h-4 accent-honey" />
              <span className="text-ink/75 text-sm">Track stock (block sales at 0)</span>
            </label>
          </div>
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-3">Shipping</h3>
          <div className="grid grid-cols-4 gap-4">
            <Field label="Weight (oz)" hint="Used for shipping rates">
              <input type="number" step="0.1" min="0" value={form.weight_oz ?? ''} onChange={e => set('weight_oz', numOrNull(e.target.value))} className={inputCls} />
            </Field>
            <Field label="Length (in)"><input type="number" step="0.1" min="0" value={form.length_in ?? ''} onChange={e => set('length_in', numOrNull(e.target.value))} className={inputCls} /></Field>
            <Field label="Width (in)"><input type="number" step="0.1" min="0" value={form.width_in ?? ''} onChange={e => set('width_in', numOrNull(e.target.value))} className={inputCls} /></Field>
            <Field label="Height (in)"><input type="number" step="0.1" min="0" value={form.height_in ?? ''} onChange={e => set('height_in', numOrNull(e.target.value))} className={inputCls} /></Field>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-ink/75 text-sm font-bold">Images</h3>
            <button type="button" onClick={() => setShowMockups(true)} className="flex items-center gap-1 text-honey-700 text-xs font-bold hover:underline">
              <Wand2 className="w-3 h-3" /> Generate mockups
            </button>
          </div>
          <ImageManager images={form.images} onChange={imgs => set('images', imgs)} />
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-1">Options</h3>
          <p className="text-ink/45 text-xs mb-3">Up to 5 choices shoppers make, like size, color, or style. Stock is tracked for the product as a whole.</p>
          <OptionsEditor options={form.options || []} onChange={v => set('options', v)} />
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-1">Personalization</h3>
          <p className="text-ink/45 text-xs mb-3">Text the shopper types in, like a name or monogram. It appears on the order and packing slip.</p>
          <PersonalizationEditor fields={form.personalizations || []} onChange={v => set('personalizations', v)} />
        </section>

        <section>
          <h3 className="text-ink/75 text-sm font-bold mb-3">Search engine listing</h3>
          <div className="space-y-3">
            <Field label={`SEO title (${(form.seo_title || '').length}/60)`} hint="Leave blank to use the product title">
              <input value={form.seo_title || ''} onChange={e => set('seo_title', e.target.value)} maxLength={120} className={inputCls} />
            </Field>
            <Field label={`Meta description (${(form.seo_description || '').length}/155)`}>
              <textarea value={form.seo_description || ''} onChange={e => set('seo_description', e.target.value)} rows={2} maxLength={320} className={`${inputCls} resize-none`} />
            </Field>
            <div className="p-3 rounded-xl bg-white text-left">
              <p className="text-[#1a0dab] text-base truncate">{form.seo_title || form.title || 'Product title'}</p>
              <p className="text-[#006621] text-xs">market.buzzzillion.com › products</p>
              <p className="text-[#545454] text-sm line-clamp-2">{form.seo_description || (form.description || '').slice(0, 155) || 'Meta description preview'}</p>
            </div>
          </div>
        </section>

        <section className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => set('published', e.target.checked)} className="w-4 h-4 accent-honey" />
            <span className="text-ink/75 text-sm">Published (visible in store)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={e => set('featured', e.target.checked)} className="w-4 h-4 accent-honey" />
            <span className="text-ink/75 text-sm">Featured</span>
          </label>
        </section>

        <BundleSection items={form.bundle_items || []} price={form.price} productId={product?.id} defaultOpen={startAsBundle}
          onChange={v => set('bundle_items', v)} />

        <details className="rounded-xl border border-ink/10 p-4" open={form.fulfillment !== 'in_house' || !!form.supplier}>
          <summary className="text-ink/75 text-sm font-bold cursor-pointer">Sourcing &amp; fulfillment <span className="text-ink/45 font-normal">(internal only)</span></summary>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <Field label="Fulfillment">
              <Select value={form.fulfillment || 'in_house'} onChange={e => set('fulfillment', e.target.value)} className={`${selectCls} w-full`}>
                <option value="in_house">Ship from my stock</option>
                <option value="dropship">Dropship (supplier ships)</option>
                <option value="print_on_demand">Print on demand</option>
              </Select>
            </Field>
            <Field label="Supplier"><input value={form.supplier || ''} onChange={e => set('supplier', e.target.value)} className={inputCls} /></Field>
            <Field label="Supplier SKU"><input value={form.supplier_sku || ''} onChange={e => set('supplier_sku', e.target.value)} className={inputCls} /></Field>
          </div>
        </details>

        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="save-product-btn" className={`${btnPrimary} flex-1 py-2.5`}>
            {saving ? 'Saving...' : 'Save Product'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ── CSV import ────────────────────────────────────────────────────
function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async (dryRun) => {
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API}/admin/products/import`, form, { params: { dry_run: dryRun }, ...withCreds });
      if (dryRun) setPreview(r.data);
      else { setResult(r.data); onDone(); }
    } catch (e) { setError(errMsg(e, 'Import failed')); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Import products from CSV" onClose={onClose} width="max-w-3xl">
      {result ? (
        <div className="space-y-3">
          <p className="text-green-600 font-bold">Import complete: {result.created} created, {result.updated} updated.</p>
          {result.errors.length > 0 && <p className="text-amber-600 text-sm">{result.errors.length} row(s) were skipped because of errors.</p>}
          <button onClick={onClose} className={btnSecondary}>Close</button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-ink/60 text-sm space-y-1">
            <p>Rows are matched to existing products by <b className="text-ink/85">id</b>, then by <b className="text-ink/85">sku</b>. Unmatched rows create new products, which need a title and price.</p>
            <p>Blank cells leave the current value alone. Separate multiple <b className="text-ink/85">tags</b> or <b className="text-ink/85">images</b> with <code className="text-honey-700">|</code>.</p>
            <p>Tip: export your products first, edit that file in a spreadsheet, and import it back.</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input type="file" accept=".csv,text/csv" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="text-ink/75 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-ink/[0.07] file:text-ink" />
            <button type="button" onClick={() => downloadCsv('/admin/products/import-template')} className={btnSecondary}>
              <FileSpreadsheet className="w-4 h-4" /> Template
            </button>
          </div>
          {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
          {!preview ? (
            <button disabled={!file || busy} onClick={() => send(true)} className={btnPrimary}>{busy ? 'Checking…' : 'Preview import'}</button>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-600">{preview.created} new</span>
                <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-600">{preview.updated} updates</span>
                <span className={`px-3 py-1 rounded-full ${preview.errors.length ? 'bg-red-500/10 text-red-600' : 'bg-ink/[0.04] text-ink/55'}`}>{preview.errors.length} errors</span>
              </div>
              {preview.ignored_columns?.length > 0 && (
                <p className="text-amber-600 text-xs">Ignoring unknown columns: {preview.ignored_columns.join(', ')}</p>
              )}
              {preview.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-xl border border-red-500/20">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-ink/5">
                      {preview.errors.map((e, i) => (
                        <tr key={i} className="text-ink/75"><td className="p-2 text-ink/55">Row {e.row}</td><td className="p-2">{e.sku || e.title}</td><td className="p-2 text-red-600">{e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.rows.length > 0 && (
                <div className="max-h-56 overflow-y-auto rounded-xl border border-ink/10">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-ink/5">
                      {preview.rows.map(r => (
                        <tr key={r.row} className="text-ink/75">
                          <td className="p-2 text-ink/55">Row {r.row}</td>
                          <td className="p-2"><span className={r.action === 'create' ? 'text-green-600' : 'text-blue-600'}>{r.action}</span></td>
                          <td className="p-2 font-mono">{r.sku}</td><td className="p-2">{r.title}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2">
                <button disabled={busy || (preview.created + preview.updated === 0)} onClick={() => send(false)} className={btnPrimary}>
                  {busy ? 'Importing…' : `Import ${preview.created + preview.updated} product(s)`}
                </button>
                <button onClick={() => setPreview(null)} className={btnSecondary}>Back</button>
              </div>
              {preview.errors.length > 0 && <p className="text-ink/45 text-xs">Rows with errors will be skipped.</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Bulk action bar ───────────────────────────────────────────────
const BULK_ACTIONS = [
  ['category', 'Set category'], ['published', 'Set visibility'], ['featured', 'Set featured'],
  ['price', 'Set price'], ['price_adjust_percent', 'Adjust price by %'], ['compare_price', 'Set compare-at price'],
  ['inventory', 'Set stock'], ['inventory_adjust', 'Add / remove stock'], ['weight_oz', 'Set weight (oz)'],
  ['add_tags', 'Add tags'], ['remove_tags', 'Remove tags'], ['track_inventory', 'Stock tracking'],
];
const BOOL_OPTIONS = {
  published: [['true', 'Published'], ['false', 'Draft']],
  featured: [['true', 'Featured'], ['false', 'Not featured']],
  track_inventory: [['true', 'Track stock'], ['false', "Don't track"]],
};
const NUMBER_ACTIONS = ['price', 'compare_price', 'price_adjust_percent', 'inventory', 'inventory_adjust', 'weight_oz'];

function BulkBar({ count, categories, onApply, onDelete, onClear, onAIFill, busy }) {
  const ai = useAIStatus();
  const [action, setAction] = useState('');
  const [value, setValue] = useState('');

  const apply = async () => {
    let v = value;
    if (['price', 'compare_price', 'price_adjust_percent', 'weight_oz'].includes(action)) v = value === '' ? null : parseFloat(value);
    if (['inventory', 'inventory_adjust'].includes(action)) v = parseInt(value);
    if (BOOL_OPTIONS[action]) v = value === 'true';
    if (['add_tags', 'remove_tags'].includes(action)) v = value.split(',').map(t => t.trim()).filter(Boolean);
    await onApply({ [action]: v });
    setAction(''); setValue('');
  };

  return (
    <div className="flex items-center gap-2 flex-wrap w-full border-t border-ink/10 pt-3 mt-1">
      <span className="text-honey-700 text-sm font-bold">{count} selected</span>
      <Select value={action} onChange={e => { setAction(e.target.value); setValue(''); }} data-testid="bulk-action-select" className={selectCls}>
        <option value="">Choose a bulk action…</option>
        {BULK_ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </Select>
      {action === 'category' && (
        <>
          <input value={value} onChange={e => setValue(e.target.value)} list="bulk-categories" placeholder="Category name" className={`${smallInputCls} w-44`} />
          <datalist id="bulk-categories">{categories.map(c => <option key={c.id} value={c.name} />)}</datalist>
        </>
      )}
      {BOOL_OPTIONS[action] && (
        <Select value={value} onChange={e => setValue(e.target.value)} className={selectCls}>
          <option value="">Choose…</option>
          {BOOL_OPTIONS[action].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
      )}
      {NUMBER_ACTIONS.includes(action) && (
        <input type="number" step="any" value={value} onChange={e => setValue(e.target.value)} className={`${smallInputCls} w-32`}
          placeholder={{ price_adjust_percent: 'e.g. 10 or -15', inventory_adjust: 'e.g. 12 or -3', compare_price: 'blank = clear' }[action] || ''} />
      )}
      {['add_tags', 'remove_tags'].includes(action) && (
        <input value={value} onChange={e => setValue(e.target.value)} placeholder="tag1, tag2" className={`${smallInputCls} w-44`} />
      )}
      {action && (
        <button onClick={apply} disabled={busy || (value === '' && action !== 'compare_price')} data-testid="bulk-edit-btn"
          className="flex items-center gap-1 px-3 py-1.5 bg-honey text-black rounded-lg text-sm font-bold hover:bg-honey-600 disabled:opacity-40">
          <Check className="w-3 h-3" /> Apply
        </button>
      )}
      {ai?.copy && (
        <button onClick={onAIFill} disabled={busy} title="Write a description, tags and SEO for products that are missing them. Existing text is never overwritten."
          className="flex items-center gap-1 px-3 py-1.5 bg-honey/10 text-honey-700 border border-honey/20 rounded-lg text-sm font-bold hover:bg-honey/20 disabled:opacity-40">
          <Sparkles className="w-3 h-3" /> AI: fill in missing copy
        </button>
      )}
      <button onClick={onDelete} disabled={busy} data-testid="bulk-delete-btn" className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-600 border border-red-500/20 rounded-lg text-sm font-bold hover:bg-red-500/20 disabled:opacity-40 ml-auto">
        <Trash2 className="w-3 h-3" /> Delete
      </button>
      <button onClick={onClear} className="text-ink/45 hover:text-ink"><X className="w-4 h-4" /></button>
    </div>
  );
}

// ── Inline grid cell ──────────────────────────────────────────────
function Cell({ value, onChange, type = 'text', list, width = 'w-20' }) {
  return (
    <input type={type} step={type === 'number' ? 'any' : undefined} value={value ?? ''} list={list}
      onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
      className={`${width} bg-ink/[0.04] border border-ink/10 rounded-md px-2 py-1 text-ink text-xs focus:outline-none focus:border-honey`} />
  );
}

// ── Page ──────────────────────────────────────────────────────────
export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [startAsBundle, setStartAsBundle] = useState(false);
  const [filters, setFilters] = useState({ search: '', category: searchParams.get('category') || '', status: '', stock: searchParams.get('stock') || '', sort: 'newest' });
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [selected, setSelected] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [gridMode, setGridMode] = useState(false);
  const [edits, setEdits] = useState({}); // { productId: { field: value } }
  const [gridErrors, setGridErrors] = useState({});
  const [aiProgress, setAiProgress] = useState(null); // { done, total, failed }

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/products`, { params: { ...filters, page, limit }, ...withCreds });
      setProducts(r.data.products);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [filters, page, limit]);

  const fetchCategories = async () => {
    try { setCategories((await axios.get(`${API}/admin/categories`, withCreds)).data.categories); } catch {}
  };

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchCategories(); }, []);

  // Deep links from other admin pages: ?edit=<id> opens a product, ?new=bundle starts a bundle listing
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) axios.get(`${API}/admin/products/${editId}`, withCreds).then(r => setEditProduct(r.data)).catch(() => {});
    if (searchParams.get('new') === 'bundle') { setStartAsBundle(true); setShowAdd(true); }
    if (editId || searchParams.get('new')) setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); setSelected([]); };
  const filtered = filters.search || filters.category || filters.status || filters.stock;
  const editCount = Object.keys(edits).length;

  const handleSave = async (form) => {
    if (editProduct?.id) await axios.put(`${API}/admin/products/${editProduct.id}`, form, withCreds);
    else await axios.post(`${API}/admin/products`, form, withCreds);
    setShowAdd(false); setEditProduct(null); setStartAsBundle(false);
    fetchProducts(); fetchCategories();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/admin/products/${id}`, withCreds);
    fetchProducts();
  };

  const handleDuplicate = async (id) => {
    const r = await axios.post(`${API}/admin/products/${id}/duplicate`, {}, withCreds);
    fetchProducts();
    setEditProduct(r.data);
  };

  const bulkApply = async (updates) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/products/bulk-edit`, { product_ids: selected, updates }, withCreds);
      setSelected([]);
      fetchProducts(); fetchCategories();
    } catch (e) { alert(errMsg(e, 'Bulk edit failed')); }
    finally { setBusy(false); }
  };

  const bulkAIFill = async () => {
    if (!window.confirm(`Have AI write a description, tags and SEO text for ${selected.length} product(s)? Only empty fields are filled in. This takes about 15-30 seconds per product.`)) return;
    setBusy(true);
    const ids = [...selected];
    const progress = { done: 0, total: ids.length, failed: [] };
    setAiProgress({ ...progress });
    for (const id of ids) {
      try {
        await axios.post(`${API}/admin/ai/product-copy`, { product_id: id, apply_fields: ['description', 'tags', 'seo_title', 'seo_description'], only_if_empty: true }, withCreds);
      } catch (e) {
        progress.failed.push(`${products.find(p => p.id === id)?.title || id}: ${errMsg(e)}`);
      }
      progress.done += 1;
      setAiProgress({ ...progress });
    }
    setBusy(false);
    setSelected([]);
    fetchProducts();
    if (progress.failed.length) alert(`Finished with ${progress.failed.length} problem(s):\n\n${progress.failed.join('\n')}`);
    setAiProgress(null);
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected product${selected.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/products/bulk-delete`, { product_ids: selected }, withCreds);
      setSelected([]);
      fetchProducts();
    } finally { setBusy(false); }
  };

  const editCell = (id, field, value) => setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));
  const cellValue = (p, field) => (edits[p.id] && field in edits[p.id] ? edits[p.id][field] : p[field]);

  const saveGrid = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/products/bulk-update`, { updates: Object.entries(edits).map(([id, f]) => ({ id, ...f })) }, withCreds);
      const errs = Object.fromEntries(r.data.errors.map(e => [e.id, e.error]));
      setGridErrors(errs);
      setEdits(e => Object.fromEntries(Object.entries(e).filter(([id]) => errs[id])));
      fetchProducts(); fetchCategories();
    } catch (e) { alert(errMsg(e, 'Save failed')); }
    finally { setBusy(false); }
  };

  const leaveGrid = () => {
    if (editCount && !window.confirm('Discard unsaved changes?')) return;
    setEdits({}); setGridErrors({}); setGridMode(false);
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === products.length ? [] : products.map(p => p.id));
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <AdminLayout>
      {(showAdd || editProduct) && (
        <ProductModal product={editProduct} categories={categories} startAsBundle={startAsBundle && !editProduct}
          onClose={() => { setShowAdd(false); setEditProduct(null); setStartAsBundle(false); }} onSave={handleSave} />
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { fetchProducts(); fetchCategories(); }} />}

      <PageHeader title="Products" subtitle={`${total} product${total === 1 ? '' : 's'}${filtered ? ' match these filters' : ''}`}>
        <button onClick={() => setShowImport(true)} className={btnSecondary}><Upload className="w-4 h-4" /> Import CSV</button>
        <button onClick={() => downloadCsv('/admin/products/export', filters).catch(e => alert(errMsg(e)))} className={btnSecondary}><Download className="w-4 h-4" /> Export CSV</button>
        <button onClick={() => (gridMode ? leaveGrid() : setGridMode(true))} className={gridMode ? `${btnSecondary} !border-honey/40 !text-honey-700` : btnSecondary}>
          <Table2 className="w-4 h-4" /> {gridMode ? 'Exit quick edit' : 'Quick edit'}
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="add-product-btn" className={btnPrimary}><Plus className="w-4 h-4" /> Add Product</button>
      </PageHeader>

      {/* Filters */}
      <div className="admin-card rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
          <input value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && setFilter('search', searchText)}
            onBlur={() => searchText !== filters.search && setFilter('search', searchText)}
            placeholder="Search title, SKU, tag, supplier…" data-testid="admin-search-input"
            className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-10 pr-4 py-2 text-ink text-sm focus:outline-none focus:border-honey" />
        </div>
        <Select value={filters.category} onChange={e => setFilter('category', e.target.value)} className={selectCls}>
          <option value="">All categories</option>
          <option value="__none__">Uncategorized</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name} ({c.product_count})</option>)}
        </Select>
        <Select value={filters.status} onChange={e => setFilter('status', e.target.value)} className={selectCls}>
          <option value="">Any status</option><option value="published">Published</option><option value="draft">Draft</option><option value="bundle">Bundles</option>
        </Select>
        <Select value={filters.stock} onChange={e => setFilter('stock', e.target.value)} className={selectCls}>
          <option value="">Any stock</option><option value="low">Low stock</option><option value="out">Out of stock</option>
        </Select>
        <Select value={filters.sort} onChange={e => setFilter('sort', e.target.value)} className={selectCls}>
          <option value="newest">Newest</option><option value="oldest">Oldest</option><option value="title">Title A–Z</option>
          <option value="sku">SKU</option><option value="price_asc">Price ↑</option><option value="price_desc">Price ↓</option>
          <option value="stock_asc">Stock ↑</option><option value="stock_desc">Stock ↓</option>
        </Select>
        {selected.length > 0 && !gridMode && (
          <BulkBar count={selected.length} categories={categories} busy={busy} onApply={bulkApply} onDelete={bulkDelete} onAIFill={bulkAIFill} onClear={() => setSelected([])} />
        )}
        {aiProgress && (
          <div className="w-full flex items-center gap-3 text-sm text-honey-700">
            <Sparkles className="w-4 h-4 animate-pulse" /> AI writing {aiProgress.done} of {aiProgress.total}…
            <div className="flex-1 h-1 bg-ink/[0.07] rounded-full overflow-hidden"><div className="h-full bg-honey" style={{ width: `${(aiProgress.done / aiProgress.total) * 100}%` }} /></div>
          </div>
        )}
        {gridMode && (
          <div className="flex items-center gap-3 w-full border-t border-ink/10 pt-3 mt-1">
            <p className="text-ink/55 text-xs">Edit cells directly, then save all changes at once.</p>
            <span className="text-honey-600 text-sm font-bold ml-auto">{editCount} row{editCount === 1 ? '' : 's'} changed</span>
            <button onClick={() => { setEdits({}); setGridErrors({}); }} disabled={!editCount} className={btnSecondary}>Discard</button>
            <button onClick={saveGrid} disabled={!editCount || busy} className={btnPrimary}><Check className="w-4 h-4" /> {busy ? 'Saving…' : 'Save changes'}</button>
          </div>
        )}
      </div>

      <div className="admin-card rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-ink/10">
            <tr className="text-ink/55 text-xs uppercase tracking-widest">
              {!gridMode && <th className="text-left p-3"><input type="checkbox" checked={selected.length === products.length && products.length > 0} onChange={toggleAll} className="accent-honey" /></th>}
              <th className="text-left p-3">Product</th>
              <th className="text-left p-3">SKU</th>
              <th className="text-left p-3">Price</th>
              {gridMode && <th className="text-left p-3">Compare</th>}
              {gridMode && <th className="text-left p-3">Cost</th>}
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Stock</th>
              {gridMode && <th className="text-left p-3">Wt (oz)</th>}
              <th className="text-left p-3">{gridMode ? 'Live' : 'Status'}</th>
              {!gridMode && <th className="text-right p-3">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {loading ? (
              <tr><td colSpan={10}><Spinner /></td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-12">
                <Package className="w-8 h-8 text-ink/30 mx-auto mb-2" />
                <p className="text-ink/45 text-sm">No products found.</p>
              </td></tr>
            ) : products.map(p => {
              const tracked = p.track_inventory !== false;
              const stockCls = !tracked ? 'text-ink/45' : p.inventory <= 0 ? 'text-red-600 font-bold' : p.inventory <= 5 ? 'text-amber-600 font-bold' : 'text-ink/70';
              return (
                <tr key={p.id} className={`text-ink/75 hover:bg-ink/[0.04] transition-colors ${edits[p.id] ? 'bg-honey/5' : ''}`} data-testid={`product-row-${p.id}`}>
                  {!gridMode && <td className="p-3"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="accent-honey" /></td>}
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-ink/[0.04] flex-shrink-0">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-ink/30 m-auto mt-2.5" />}
                      </div>
                      <div className="min-w-0">
                        {gridMode ? <Cell value={cellValue(p, 'title')} onChange={v => editCell(p.id, 'title', v)} width="w-48" />
                          : <button onClick={() => setEditProduct(p)} className="text-ink font-medium text-sm truncate max-w-[240px] block text-left hover:text-honey-700">{p.title}</button>}
                        {gridErrors[p.id] && <p className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{gridErrors[p.id]}</p>}
                        {!gridMode && p.bundle_items?.length > 0 && <p className="text-honey-600/70 text-xs">Bundle of {p.bundle_items.reduce((n, i) => n + i.quantity, 0)} items</p>}
                        {!gridMode && (p.options?.length > 0 || p.personalizations?.length > 0) && (
                          <p className="text-ink/45 text-xs">{[p.options?.length ? p.options.map(o => o.name).join(' · ') : '', p.personalizations?.length ? 'Personalized' : ''].filter(Boolean).join(' · ')}</p>
                        )}
                        {!gridMode && p.fulfillment && p.fulfillment !== 'in_house' && (
                          <p className="text-ink/45 text-xs">{p.fulfillment === 'dropship' ? 'Dropship' : 'Print on demand'}{p.supplier ? ` · ${p.supplier}` : ''}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs">{gridMode ? <Cell value={cellValue(p, 'sku')} onChange={v => editCell(p.id, 'sku', v)} width="w-28" /> : (p.sku || <span className="text-ink/30">—</span>)}</td>
                  <td className="p-3">
                    {gridMode ? <Cell type="number" value={cellValue(p, 'price')} onChange={v => editCell(p.id, 'price', v ?? 0)} /> : (
                      <>
                        <span className="text-ink font-bold">{money(p.price)}</span>
                        {p.compare_price && <span className="text-ink/45 text-xs line-through ml-1">{money(p.compare_price)}</span>}
                      </>
                    )}
                  </td>
                  {gridMode && <td className="p-3"><Cell type="number" value={cellValue(p, 'compare_price')} onChange={v => editCell(p.id, 'compare_price', v)} /></td>}
                  {gridMode && <td className="p-3"><Cell type="number" value={cellValue(p, 'cost')} onChange={v => editCell(p.id, 'cost', v)} /></td>}
                  <td className="p-3 text-ink/60 text-xs">
                    {gridMode ? <Cell value={cellValue(p, 'category')} onChange={v => editCell(p.id, 'category', v)} list="grid-categories" width="w-32" /> : (p.category || '—')}
                  </td>
                  <td className={`p-3 text-xs ${stockCls}`}>
                    {gridMode ? <Cell type="number" value={cellValue(p, 'inventory')} onChange={v => editCell(p.id, 'inventory', v ?? 0)} width="w-16" /> : (tracked ? p.inventory : '∞')}
                  </td>
                  {gridMode && <td className="p-3"><Cell type="number" value={cellValue(p, 'weight_oz')} onChange={v => editCell(p.id, 'weight_oz', v)} width="w-16" /></td>}
                  <td className="p-3">
                    {gridMode ? (
                      <input type="checkbox" checked={!!cellValue(p, 'published')} onChange={e => editCell(p.id, 'published', e.target.checked)} className="accent-honey" title="Published" />
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${p.published ? 'bg-green-500/10 text-green-600' : 'bg-ink/[0.04] text-ink/45'}`}>{p.published ? 'Live' : 'Draft'}</span>
                    )}
                  </td>
                  {!gridMode && (
                    <td className="p-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setEditProduct(p)} title="Edit" data-testid={`edit-product-${p.id}`} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDuplicate(p.id)} title="Duplicate" className="text-ink/55 hover:text-honey-600"><Copy className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(p.id)} title="Delete" data-testid={`delete-product-${p.id}`} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        <datalist id="grid-categories">{categories.map(c => <option key={c.id} value={c.name} />)}</datalist>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <Select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className={selectCls}>
          {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n} per page</option>)}
        </Select>
        {pages > 1 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className={btnSecondary}>Prev</button>
            <span className="text-ink/55 text-sm">Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className={btnSecondary}>Next</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
